import axios from 'axios';
import {
  GET_COMPANIES,
  GET_ROLE_GROUPS,
  GET_USER_ROLES,
  GET_USERS,
  LIST_ENTITY_CONFIGS_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';
import { fetch_entity_profiles, is_real_case_record } from '../CompanyCases/company_cases_helpers';
import {
  PORTFOLIO_COLORS,
  parse_api_data,
  text,
  to_array,
  unique_values
} from './company_portfolio_common';
import { role_name } from './company_portfolio_formatters';
import { scope_from_project, scopes_from_role_groups } from './company_portfolio_scope_helpers';

function project_number(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
}

function record_project_id(record = {}) {
  return project_number(
    record?.project_id
    || record?.project?.id
    || record?.project?.project_id
    || record?.portfolio_project_id
  );
}

function project_case_records(records = [], project_id) {
  return records.filter((record) => record_project_id(record) === project_id);
}

async function fetch_portfolio_cases() {
  const records = await fetch_entity_profiles({ page_size: 100 });
  return records.filter((record) => is_real_case_record(record));
}

async function fetch_project_roles(project_id) {
  const response = await axios.get(
    GET_USER_ROLES,
    get_request_config({ params: { project_id } })
  );
  return to_array(parse_api_data(response.data)?.data).map(role_name).filter(Boolean);
}

async function fetch_role_groups() {
  const response = await axios.get(GET_ROLE_GROUPS, get_request_config());
  return response_items(response.data, ['role_groups']);
}

async function fetch_roles() {
  const response = await axios.get(GET_USER_ROLES, get_request_config());
  return response_items(response.data, ['roles']);
}

async function fetch_users() {
  const users = [];
  let page = 1;
  let total_pages = 1;

  do {
    const response = await axios.get(
      GET_USERS,
      get_request_config({ params: { page, page_size: 100 } })
    );
    const data = parse_api_data(response.data);
    users.push(...response_items(response.data, ['users']));
    total_pages = Number(data?.pagination?.total_pages || 1);
    page += 1;
  } while (page <= total_pages);

  return users;
}

async function fetch_project_configs(project_id) {
  const response = await axios.get(
    LIST_ENTITY_CONFIGS_URL,
    get_request_config({ params: { project_id, is_active: true } })
  );
  return to_array(parse_api_data(response.data)?.data);
}

function response_items(response_data, keys = []) {
  const data = parse_api_data(response_data);
  const nested_data = parse_api_data(data.data);
  const candidates = [
    nested_data,
    ...keys.map((key) => data[key]),
    data
  ];
  return to_array(candidates.find((candidate) => to_array(candidate).length));
}

function configured(values) {
  return unique_values(values).join(' · ') || 'Not configured';
}

function scope_lookup(scopes = []) {
  return new Map(scopes.map((scope) => [Number(scope.id), scope]));
}

function numeric_count(value) {
  const digits = String(value || '').replace(/[^\d.-]/g, '');
  return Number(digits || 0);
}

function group_roles(group, roles = []) {
  const group_id = Number(group.id || group.group_id);
  const group_name = text(group.name || group.group_name);
  return roles
    .filter((role) =>
      Number(role.group_id || role.group?.id || role.group) === group_id
      || text(role.group_name || role.group?.name) === group_name
    )
    .map((role) => text(role.role || role.role_name || role.name))
    .filter(Boolean);
}

function role_group_payload(group, scope, roles, index) {
  const grouped_roles = group_roles(group, roles);
  const payload = Object.fromEntries([]);
  payload.id = Number(group.id || group.group_id);
  payload.name = text(group.name || group.group_name || 'Unnamed role group');
  payload.active = numeric_count(scope?.active);
  payload.color = PORTFOLIO_COLORS[index % PORTFOLIO_COLORS.length];
  payload.details = [
    { label: 'Description', value: text(group.description) || 'No description available' },
    { label: 'Roles', value: configured(grouped_roles) },
    { label: 'Coverage', value: text(scope?.meta) || 'Not configured' },
    { label: 'SLA', value: text(scope?.sla_ok) || '100%' }
  ];
  return payload;
}

function role_group_payloads(groups = [], scopes = [], roles = []) {
  const scopes_by_id = scope_lookup(scopes);
  return groups
    .filter((group) => Number(group.id || group.group_id) && text(group.name || group.group_name))
    .map((group, index) => role_group_payload(group, scopes_by_id.get(Number(group.id || group.group_id)), roles, index));
}

async function project_result(project, portfolio_cases = []) {
  const project_id = project_number(project.id || project.project_id);
  const [roles, configs] = await Promise.all([
    fetch_project_roles(project_id),
    fetch_project_configs(project_id)
  ]);
  const records = project_case_records(portfolio_cases, project_id);
  const result = Object.fromEntries([]);
  result.records = records.map((record) => portfolio_record(record, project, project_id));
  result.scope = scope_from_project(project, result.records, roles, configs);
  return result;
}

function portfolio_record(record, project, project_id) {
  return {
    ...record,
    portfolio_project_id: project_id,
    portfolio_project_name: text(project.name || project.project_name)
  };
}

export async function fetch_portfolio_data() {
  const [role_groups, roles, users, response, portfolio_cases] = await Promise.all([
    fetch_role_groups(),
    fetch_roles(),
    fetch_users(),
    axios.get(GET_COMPANIES, get_request_config()),
    fetch_portfolio_cases(),
  ]);
  const data = parse_api_data(response.data);
  const projects = to_array(data.data || data.projects)
    .filter((project) => project.is_sub_program !== true);
  const portfolio = Object.fromEntries([]);
  portfolio.projects = projects;
  portfolio.results = await Promise.all(
    projects.map((project) => project_result(project, portfolio_cases))
  );
  portfolio.scopes = scopes_from_role_groups({
    groups: role_groups,
    roles,
    users,
    records: portfolio.results.flatMap((result) => result.records)
  });
  portfolio.role_group_configs = role_group_payloads(role_groups, portfolio.scopes, roles);
  return portfolio;
}
